import { isDefined } from '@aloft/toolkit-shared';
import { Block, Flex, Typography, getSortIcon } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { ChakraProps, chakra } from '@aloft/toolkit-sui/chakra';
import { Row as ReactTableRow, Table, flexRender } from '@tanstack/react-table';

import { useCallback, useRef } from 'react';

import { MemoizedRow } from './components/MemoizedRow.tsx';
import { Row } from './components/Row.tsx';
import { HEADER_HEIGHT_IN_PX, PADDING_X_IN_PX, sharedCellStyles } from './constants.ts';
import { getCellWidthStyles } from './getCellWidthStyles.ts';
import { TPTableClickRow } from './types.ts';
import { usePinnedColumnStyles } from './usePinnedColumnStyles.ts';
import { useVirtualizedRows } from './useVirtualizedRows.ts';
import { makeRowID } from './utils/makeRowID.ts';

type Props<T> = {
  table: Table<T>;
  shouldPinFirstRow: boolean | number;
  activeRowIndex?: number | null;
  emptyTableMessage?: string;
  onRowClick?: TPTableClickRow<T>;
  __dangerOverrideInternalSelectionState?: boolean;
  memoizeRows?: boolean;
  getRowBackgroundColor?: (row: ReactTableRow<T>) => string | undefined;
};

export const ToolkitPropertyTableUI = <T,>({
  table,
  shouldPinFirstRow,
  activeRowIndex = null,
  emptyTableMessage = 'No properties found',
  onRowClick,
  __dangerOverrideInternalSelectionState = false,
  memoizeRows = false,
  getRowBackgroundColor,
}: Props<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const numPinnedColumns = table.getState().columnPinning.left?.length ?? 0;
  const pinnedColumnStyles = usePinnedColumnStyles({
    paddingX: PADDING_X_IN_PX,
    numColumns: numPinnedColumns,
  });

  const { rows: reactTableRows } = table.getRowModel();
  const { virtualRows, virtualPaddingTop, virtualPaddingBottom } = useVirtualizedRows({
    numRows: reactTableRows.length,
    shouldPinFirstRow,
    containerRef,
    overscan: 7,
  });

  const handleRowClick = useCallback(
    (row: ReactTableRow<T>) => {
      onRowClick?.(row.original);
    },
    [onRowClick],
  );

  const rows = memoizeRows ? reactTableRows : virtualRows;
  const isTableEmpty = rows.length === 0;
  const RowComponent = memoizeRows ? MemoizedRow : Row;

  return (
    <Block ref={containerRef} height="100%" overflow="auto" position="relative">
      <chakra.table width="100%" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <chakra.thead position="sticky" top="0px" zIndex="1" height={`${HEADER_HEIGHT_IN_PX}px`}>
          {table.getHeaderGroups().map((headerGroup) => (
            <chakra.tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                pinnedColumnStyles.setStyleInfo(header.column);

                const styles: ChakraProps = {
                  ...sharedCellStyles,
                  ...pinnedColumnStyles.getStyles(header.column),
                  ...getCellWidthStyles(header.getSize()),
                  backgroundColor: 'surface2',
                  borderBottom: '2px solid',
                  borderColor: 'strokeDefault',
                  verticalAlign: 'middle',
                };
                const content = flexRender(header.column.columnDef.header, header.getContext());

                return (
                  <chakra.th key={header.id} {...styles}>
                    <Flex
                      onClick={header.column.getToggleSortingHandler()}
                      cursor={canSort ? 'pointer' : undefined}
                      alignItems="center"
                      gap={1}
                    >
                      {content}
                      {canSort && getSortIcon(header.column)}
                    </Flex>
                  </chakra.th>
                );
              })}
            </chakra.tr>
          ))}
        </chakra.thead>
        <chakra.tbody>
          {!memoizeRows && virtualPaddingTop && (
            <tr>
              <chakra.td height={virtualPaddingTop} />
            </tr>
          )}
          {rows.map((x) => {
            const row = memoizeRows ? (x as ReactTableRow<T>) : reactTableRows[x.index];
            if (!row) return null;

            return (
              <RowComponent
                key={row.id}
                id={__dangerOverrideInternalSelectionState ? undefined : makeRowID(row.index)}
                isSelected={
                  __dangerOverrideInternalSelectionState
                    ? null
                    : !!table.getSelectedRowModel().rowsById[row.id]
                }
                row={row}
                activeRowIndex={activeRowIndex}
                shouldPinFirstRow={shouldPinFirstRow}
                handleRowClick={handleRowClick}
                enableHoverStyle={isDefined(onRowClick)}
                pinnedColumnStyles={pinnedColumnStyles}
                customBackgroundColor={getRowBackgroundColor?.(row)}
              />
            );
          })}
          {!memoizeRows && virtualPaddingBottom && (
            <tr>
              <chakra.td height={virtualPaddingBottom} />
            </tr>
          )}
        </chakra.tbody>
      </chakra.table>
      {isTableEmpty && (
        <Flex padding={4} justifyContent="center">
          <Typography>{emptyTableMessage}</Typography>
        </Flex>
      )}
    </Block>
  );
};
